import React, { FC, useState } from 'react';
import './CalendarWidget.css';
import 'moment/locale/fr';
import { Calendar, momentLocalizer, Event as RBCEvent, SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CalendarFilter } from '../../Components';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const defaultColors = {
	background: '#fef3c7', // jaune pastel
	color: '#92400e',      // brun chaleureux
};

export interface CalendarRow {
	id: number;
	calendarDate: string;  // e.g. '2025-10-05'
	startTime?: string;     // e.g. '09:00'
	endTime?: string;       // e.g. '10:30'
	note?: string;
	type?: string;
	[key: string]: any; // pour d'autres clés dynamiques
}

export interface Resource {
	key: number | string;
	title: string;
	color?: string;
	backgroundColor?: string;
}

interface CalendarWidgetProps {
	eventsData: CalendarRow[];
	resources?: Resource[];
	filterByKey?: keyof CalendarRow; // plus de valeur par défaut
	colorByKey?: keyof CalendarRow;
	eventTitleGetter?: (event: CalendarRow) => string;
	height?: string | number;
	width?: string | number;
	views?: Array<'month' | 'week' | 'day' | 'agenda'>;
	onSelectSlot : (start : Date, end : Date) => void;
	onSelectEvent : (id : any) => void
}

const CalendarWidget: FC<CalendarWidgetProps> = ({
	eventsData,
	resources = [],
	filterByKey, // plus de valeur par défaut
	colorByKey,
	eventTitleGetter,
	height = '50vh',
	width = '45vw',
	views = ['month', 'week', 'day', 'agenda'],
	onSelectSlot,
	onSelectEvent
}) => {

	const [events, setEvents] = useState<RBCEvent[]>([]);

	
	// Style getter adapté
	const getResourceStyles = (id: string | number) => {
		const resource = resources.find(r => r.key === id);
		return {
			background: resource?.backgroundColor ?? defaultColors.background,
			color: resource?.color ?? defaultColors.color,
		};
	};

	const eventStyleGetter = (event: RBCEvent) => {
		const colorKey = colorByKey ?? filterByKey; // fallback
		const idForStyle = colorKey ? event.resource[colorKey] : undefined;

		const { background, color } = idForStyle
			? getResourceStyles(idForStyle)
			: { background: '#e5e7eb', color: '#111827' };

		return {
			style: {
				backgroundColor: background,
				color,
				borderRadius: 4,
				padding: '2px 5px',
				border: '1px solid #d1d5db',
				fontWeight: 500,
			},
		};
	};

	const updateEvents = (filteredEvents : CalendarRow[]) => {
		const events: RBCEvent[] = filteredEvents.map(row => {
			const startDateTime = row.startTime
				? new Date(`${row.calendarDate}T${row.startTime}`)
				: new Date(row.calendarDate);

			const endDateTime = row.endTime
				? new Date(`${row.calendarDate}T${row.endTime}`)
				: new Date(`${row.calendarDate}T23:59:59`);

			return {
				id: row.id,
				title: eventTitleGetter ? eventTitleGetter(row) : (row.note ?? row.type ?? 'Rendez-vous'),
				start: startDateTime,
				end: endDateTime,
				allDay: false,
				resource: row,
			};
		});
		setEvents(events);
	}

	return (
		<>
			<CalendarFilter filterByKey = {filterByKey} eventsData={eventsData} onFilterChange = {updateEvents}/>
			<div className="CalendarWidget" data-testid="CalendarWidget" style={{ height, width, margin: 'auto' }}>
				<Calendar
					localizer={localizer}
					events={events}
					startAccessor="start"
					endAccessor="end"
					titleAccessor="title"
					messages={{
						today: "Aujrd.",
						previous: 'Préc.',
						next: 'Suiv.',
						month: 'Mois',
						week: 'Sem.',
						day: 'Jour',
						agenda: 'Agenda',
						date: 'Date',
						time: 'Heure',
						event: 'Événement',
						noEventsInRange: 'Aucun événement dans cette période',
					}}
					eventPropGetter={eventStyleGetter}
					defaultView={views[0]}
					views={views}
					selectable
					onSelectSlot={(slotinfo : SlotInfo) => onSelectSlot(slotinfo.start, slotinfo.end)}
					onSelectEvent={(event : RBCEvent) => onSelectEvent(event.resource)}
				/>
			</div>
		</>
	);
};

export default CalendarWidget;